"use client"

import { type AvailableNftSort, NftSort } from "@anaconda/core/nft/search/types"
import { Trans } from "@lingui/react/macro"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { useCallback, useEffect } from "react"
import { NftSortMoleculeReatom as NftSortMolecule } from "./store.reatom"

const SORT_OPTIONS: { type: AvailableNftSort; label: React.ReactNode }[] = [
  {
    type: NftSort.LOW_PRICE_FIRST,
    label: <Trans>Price: Low to High</Trans>,
  },
  {
    type: NftSort.HIGH_PRICE_FIRST,
    label: <Trans>Price: High to Low</Trans>,
  },
  { type: NftSort.RECENTLY_LISTED, label: <Trans>Recently Listed</Trans> },
  { type: NftSort.RAREST_FIRST, label: <Trans>Most rare</Trans> },
  { type: NftSort.RAREST_LAST, label: <Trans>Least rare</Trans> },
]

interface NftSortPickerProps {
  sortOptions?: { type: AvailableNftSort; label: React.ReactNode }[]
  initialSort?: AvailableNftSort
}

export const NftSortPicker = reatomComponent<NftSortPickerProps>(
  ({ sortOptions = SORT_OPTIONS, initialSort }) => {
    const { sortAtom } = useMolecule(NftSortMolecule)
    const sort = sortAtom()
    const setSort = useCallback(
      (value: AvailableNftSort) => sortAtom.set(value),
      [sortAtom],
    )

    useEffect(() => {
      if (initialSort) {
        setSort(initialSort)
      }
    }, [initialSort, setSort])

    return (
      <Select
        value={sort}
        onValueChange={(value) => setSort(value as AvailableNftSort)}
      >
        <SelectTrigger
          className="w-auto flex-shrink-0"
          data-testid="nft-sort-picker"
        >
          <SelectValue placeholder={<Trans>Sorting</Trans>} />
        </SelectTrigger>
        <SelectContent>
          {sortOptions.map(({ type, label }) => (
            <SelectItem key={type} value={type}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  },
  "NftSortPicker",
)
